import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft,
  User,
  FileText,
  Folder,
  MessageSquare,
  MapPin,
  GraduationCap,
  FileCheck
} from 'lucide-react';
import { agentAPI } from '../../services/api';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';
import { toast } from 'sonner';

// Tab Components
import StudentPersonalForm from '../../components/agent/StudentPersonalForm';
import StudentServicesTab from '../../components/agent/StudentServicesTab';
import StudentDocumentsTab from '../../components/agent/StudentDocumentsTab';
import StudentNotesTab from '../../components/agent/StudentNotesTab';

export default function AgentStudentDetails() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const basePath = useRoleBasePath();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    fetchStudentDetails();
  }, [studentId]);

  const fetchStudentDetails = async () => {
    try {
      setLoading(true);
      const response = await agentAPI.getStudentDetails(studentId);
      setStudent(response.student);
    } catch (error) {
      console.error('Failed to fetch student details:', error);
      toast.error(error.response?.data?.error || 'Failed to load student details');
      navigate(`${basePath}/students`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AgentLayout>
    );
  }

  if (!student) {
    return (
      <AgentLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Student not found</p>
          <Button onClick={() => navigate(`${basePath}/students`)} className="mt-4">
            Back to Students
          </Button>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`${basePath}/students`)}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Student Profile</h1>
            <p className="text-gray-500 text-sm">Manage student information and services</p>
          </div>
        </div>

        {/* 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
          {/* Left Column - Summary Card (30%) */}
          <div className="lg:col-span-3">
            <Card className="p-6 bg-white border-0 shadow-sm sticky top-24">
              <div className="flex flex-col items-center text-center">
                {/* Avatar */}
                <Avatar className="h-24 w-24 mb-4">
                  <AvatarImage src={student?.avatar} />
                  <AvatarFallback className="text-2xl bg-gradient-to-br from-emerald-400 to-teal-500 text-white font-bold">
                    {student?.firstName?.[0]}{student?.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>

                {/* Name and Email */}
                <h2 className="text-xl font-semibold text-gray-900">
                  {student?.firstName} {student?.lastName}
                </h2>
                <p className="text-gray-500 text-sm mt-1">{student?.email}</p>

                {/* Info Section */}
                <div className="w-full mt-6 space-y-3">
                  <div className="flex justify-between items-center text-sm py-2 border-b border-gray-100">
                    <span className="text-gray-500 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4" />
                      University
                    </span>
                    <span className="font-medium text-gray-900 text-right max-w-[150px] truncate">
                      {student?.institution || 'Not specified'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm py-2 border-b border-gray-100">
                    <span className="text-gray-500 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Preferred Country
                    </span>
                    <span className="font-medium text-gray-900">
                      {student?.interestedCountries?.[0] || 'Not specified'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm py-2 border-b border-gray-100">
                    <span className="text-gray-500 flex items-center gap-2">
                      <Folder className="w-4 h-4" />
                      Documents
                    </span>
                    <Badge variant="outline" className="font-medium">
                      {student?.documentCount || 0} uploaded
                    </Badge>
                  </div>
                </div>

                {/* Service Metrics */}
                <div className="w-full mt-6 grid grid-cols-3 gap-2">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-blue-600">
                      {student?.serviceMetrics?.applied || 0}
                    </p>
                    <p className="text-xs text-blue-600">Applied</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-amber-600">
                      {student?.serviceMetrics?.inProgress || 0}
                    </p>
                    <p className="text-xs text-amber-600">In Progress</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-green-600">
                      {student?.serviceMetrics?.completed || 0}
                    </p>
                    <p className="text-xs text-green-600">Completed</p>
                  </div>
                </div>

                {/* Latest Remark */}
                {student?.latestNote && (
                  <div className="w-full mt-6 p-4 bg-gray-50 rounded-lg text-left">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      Latest Remark
                    </p>
                    <p className="text-sm text-gray-700 line-clamp-3">
                      {student.latestNote.text}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Right Column - Tabs (70%) */}
          <div className="lg:col-span-7">
            <Card className="bg-white border-0 shadow-sm overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="border-b border-gray-100 px-6 pt-4">
                  <TabsList className="bg-transparent p-0 h-auto gap-6">
                    <TabsTrigger
                      value="personal"
                      className="pb-3 px-0 rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none gap-2"
                    >
                      <User className="w-4 h-4" />
                      Personal
                    </TabsTrigger>
                    <TabsTrigger
                      value="services"
                      className="pb-3 px-0 rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Services
                    </TabsTrigger>
                    <TabsTrigger
                      value="documents"
                      className="pb-3 px-0 rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none gap-2"
                    >
                      <Folder className="w-4 h-4" />
                      Documents
                    </TabsTrigger>
                    <TabsTrigger
                      value="notes"
                      className="pb-3 px-0 rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none gap-2"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Notes
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="p-6">
                  <TabsContent value="personal" className="m-0">
                    <StudentPersonalForm
                      student={student}
                      onUpdate={fetchStudentDetails}
                    />
                  </TabsContent>

                  <TabsContent value="services" className="m-0">
                    <StudentServicesTab
                      studentId={studentId}
                      appliedServices={student?.appliedServices || []}
                      serviceRequests={student?.serviceRequests || []}
                      onServiceApplied={fetchStudentDetails}
                    />
                  </TabsContent>

                  <TabsContent value="documents" className="m-0">
                    <StudentDocumentsTab
                      studentId={studentId}
                      documents={student?.documents}
                      onUpdate={fetchStudentDetails}
                    />
                  </TabsContent>

                  <TabsContent value="notes" className="m-0">
                    <StudentNotesTab studentId={studentId} />
                  </TabsContent>
                </div>
              </Tabs>
            </Card>
          </div>
        </div>
      </div>
    </AgentLayout>
  );
}
